import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Container } from '@pixi/react';
import * as PIXI from 'pixi.js';
import { useCallback, useMemo, useRef } from 'react';
import { isConfigFlagEnabled } from '../../../config';
import { AVATAR_SHADOW_ALPHA } from '../../../constants/avatarConstants';
import { useSocketSend } from '../../../context/webSocket/webSocketContext';
import { addMessageBox } from '../../../redux/actions/messageBoxesActions';
import { thunkShowPlayerInfo } from '../../../redux/actions/playerInfoActions';
import { setIsSelectingSideBetTarget, thunkPlaceSideBet } from '../../../redux/actions/sideBetsActions';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { SIDE_BETS_ACCENT_COLOR_NUM } from '../../../styles/colors';
import { createOutlineFilter } from '../../../utils/pixiFilterUtils';
import { isTestEnvironment } from '../../../utils/utils';
import { PlayerBombSplatsContainer } from '../bombs/playerBombSplatsContainer';
import { AvatarDisplayContainer } from '../player/avatar/avatarDisplayContainer';
import { PlayerAnimationContainer } from '../player/playerAnimationContainer';
const OUTLINE_FILTER = createOutlineFilter(0xffffff, 6, 0.2);
const SIDE_BET_OUTLINE_FILTER = createOutlineFilter(SIDE_BETS_ACCENT_COLOR_NUM, 6, 0.2);
export const PokerSeatButtonWrapper = ({ seatIndex, bounds }) => {
    const dispatch = useAppDispatch();
    const socketSend = useSocketSend();
    const outlineFilterContainerRef = useRef(null);
    const avatarDisplayHandleRef = useRef(null);
    const isMySeat = useAppSelector((state) => state.poker.mySeatIndex === seatIndex);
    const seatedPlayerId = useAppSelector((state) => state.poker.seatedPlayers[seatIndex]?.id);
    const seatedPlayerName = useAppSelector((state) => state.poker.seatedPlayers[seatIndex]?.name);
    const isSelectingSideBetTarget = useAppSelector((state) => state.sideBets.selectSideBetTargetStartTimestampMs !== undefined);
    const isIn = useAppSelector((state) => state.poker.seatedPlayers[seatIndex]?.isIn);
    const onClick = useCallback(() => {
        if (isMySeat) {
            // clicking self
            dispatch(addMessageBox({ type: 'self-actions' }));
            return;
        }
        // clicking other player
        if (seatedPlayerName && seatedPlayerId !== undefined) {
            if (isConfigFlagEnabled('playerQuickActions')) {
                dispatch(addMessageBox({ type: 'player-quick-actions', player: { id: seatedPlayerId, name: seatedPlayerName } }));
            }
            else {
                dispatch(thunkShowPlayerInfo({ id: seatedPlayerId, name: seatedPlayerName }, socketSend));
            }
        }
    }, [dispatch, isMySeat, seatedPlayerId, seatedPlayerName, socketSend]);
    const onClickSideBet = useCallback(() => {
        dispatch(setIsSelectingSideBetTarget(false));
        if (isMySeat) {
            // clicking self, cannot side bet, defer to normal click action
            onClick();
            return;
        }
        // clicking other player
        if (seatedPlayerId !== undefined) {
            dispatch(thunkPlaceSideBet(seatedPlayerId, socketSend));
        }
    }, [dispatch, isMySeat, onClick, seatedPlayerId, socketSend]);
    const onMouseOver = useCallback(() => {
        if (outlineFilterContainerRef.current) {
            const isAvatarLoading = avatarDisplayHandleRef.current?.isLoadingRef.current;
            if (!isAvatarLoading) {
                // if avatar is loading, don't apply the outline filter because it will be applied to the loading spinner which looks bad
                const isValidSideBetPlayer = isSelectingSideBetTarget && !isMySeat && isIn;
                outlineFilterContainerRef.current.filters = isValidSideBetPlayer ? [SIDE_BET_OUTLINE_FILTER] : [OUTLINE_FILTER];
            }
        }
        // hide the shadow on the nested avatar component as it looks bad with outline:
        const avatarShadow = avatarDisplayHandleRef.current?.avatarShadowRef.current;
        if (avatarShadow) {
            avatarShadow.alpha = 0;
        }
    }, [isIn, isMySeat, isSelectingSideBetTarget]);
    const onMouseOut = useCallback(() => {
        if (outlineFilterContainerRef.current) {
            outlineFilterContainerRef.current.filters = [];
        }
        // bring the shadow on the nested avatar component back:
        const avatarShadow = avatarDisplayHandleRef.current?.avatarShadowRef.current;
        if (avatarShadow) {
            avatarShadow.alpha = AVATAR_SHADOW_ALPHA;
        }
    }, []);
    const { w, h } = bounds;
    const hitArea = useMemo(() => {
        const shortestDimension = Math.min(w, h);
        const hitAreaSize = shortestDimension * 0.8;
        // make it square:
        const hitAreaW = hitAreaSize;
        const hitAreaH = hitAreaSize;
        const x = -hitAreaW / 2;
        const y = -hitAreaH / 2;
        return new PIXI.Rectangle(x, y, hitAreaW, hitAreaH);
    }, [w, h]);
    if (!seatedPlayerName || seatedPlayerId === undefined) {
        return null;
    }
    const pixiData = isTestEnvironment()
        ? {
            type: isMySeat ? 'my-seat-background' : 'other-player-seat-button',
            seatIndex
        }
        : null;
    return (_jsx(Container, { "pixi-data": pixiData, x: 0, y: 0, cursor: 'pointer', interactive: true, pointerdown: isSelectingSideBetTarget ? onClickSideBet : onClick, mouseover: onMouseOver, mouseout: onMouseOut, accessible: true, hitArea: hitArea, children: _jsxs(PlayerAnimationContainer, { seatIndex: seatIndex, children: [_jsx(Container, { ref: outlineFilterContainerRef, children: _jsx(AvatarDisplayContainer, { ref: avatarDisplayHandleRef, seatIndex: seatIndex }) }), _jsx(PlayerBombSplatsContainer, { seatIndex: seatIndex })] }) }));
};
